/** An outline of a record's rows (the tour plan §5: the tour's days and stops; the catalog
 *  itinerary's days), whatever the levels — the record kind supplies the rows, draws each
 *  one (`renderRow`, usually on `OutlineRowShell`) and says what its keys and drops do.
 *
 *  - One tab stop (a tree with roving focus): ↑ / ↓ (Home / End) move between rows, Enter
 *    selects a row (opens it). In edit mode Alt+↑ / Alt+↓ move a row (`keyMove`), Delete
 *    removes it (`onRemoveKey`), Shift+F10 opens its actions; after a keyboard move or a
 *    removal the focus stays on the moved row, or goes to the row now in the removed one's
 *    place. The pointer drags rows by their handles (`@dnd-kit`, `drag`).
 *  - The rows come from one derived value of the draft (`useDraftSelector`): typing in a
 *    row renders that row only; the outline renders when a row appears, goes or moves.
 *  - Above `virtualizeAbove` rows (150) it is virtualised (`@tanstack/react-virtual`, fixed
 *    row heights from `rowHeight`): only the rows in view — and a few around them, the
 *    one tab stop and the row being dragged — are mounted.
 *  - `footer` (an Add button) sits under the rows in a room the record kind reserves. */
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Paper, Typography, type SxProps, type Theme } from '@mui/material';
import { defaultRangeExtractor, observeElementRect, useVirtualizer, type Range } from '@tanstack/react-virtual';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { OutlineRowProps } from './OutlineRowShell';

/** Virtualise from this many rows. */
export const VIRTUALIZE_ABOVE = 150;

/** What the outline needs of a row: its kind (the entity) and id, and whether it is a
 *  saved row the draft removed (struck: no keyboard edits, no drag). */
export interface OutlineRowBase {
  kind: string;
  id: number;
  removed: boolean;
}

export type OutlineSelection<K extends string = string> = { kind: K; id: number } | null;

export type DragPhase = 'start' | 'over' | 'end' | 'cancel';

/** Dragging rows (pointer only — Alt+arrows and the rows' menus are the keyboard path). */
export interface OutlineDrag<Row extends OutlineRowBase> {
  /** The screen reader's instructions for a drag handle. */
  instructions: string;
  /** What a screen reader hears while dragging — the rows' names, never raw ids. */
  announce: (phase: DragPhase, from: Row | undefined, over: Row | undefined, rows: readonly Row[]) => string | undefined;
  /** A drop (`over` undefined: dropped on nothing). */
  onDrop: (from: Row | undefined, over: Row | undefined, rows: readonly Row[]) => void;
  /** The dragged row's name on its floating clone; `strong` sets it in 600. */
  overlay: (row: Row) => { name: string; strong?: boolean };
}

export interface RecordOutlineProps<Row extends OutlineRowBase> {
  rows: readonly Row[];
  selection: OutlineSelection<Row['kind']>;
  onSelect: (selection: OutlineSelection<Row['kind']>) => void;
  /** A row's fixed height (the virtualiser's size; the drag overlay's). */
  rowHeight: (row: Row) => number;
  /** A row, keyed (its kind and id). */
  renderRow: (row: Row, props: OutlineRowProps) => ReactNode;
  /** Edit mode and allowed: keyboard edits and dragging. */
  live: boolean;
  /** Alt+↑ / Alt+↓ on a row it `accepts`: `move` it one place; true when it moved (the
   *  focus follows it). */
  keyMove?: { accepts: (row: Row) => boolean; move: (row: Row, delta: -1 | 1) => boolean };
  /** Delete / Backspace on a row: remove it; true when it went (the focus goes to the row
   *  now in its place). */
  onRemoveKey?: (row: Row) => boolean;
  drag?: OutlineDrag<Row>;
  labels: {
    /** The navigation landmark ("Itinerary outline"). */
    nav: string;
    /** The tree ("Days and stops"). */
    tree: string;
    /** The keyboard help (the tree's description); `liveHelp` is added in edit mode. */
    help: string;
    liveHelp?: string;
    /** With no rows ("No days yet."); `liveEmpty` is added in edit mode. */
    empty: string;
    liveEmpty?: string;
  };
  /** Under the rows (an Add button in its reserved room). */
  footer?: ReactNode;
  /** The outline's width (px — the drag overlay and the unmeasured virtual window use it —
   *  or any CSS width). Default: its container's. */
  width?: number | string;
  /** The landmark's own styles (a border, sticky placement). */
  sx?: SxProps<Theme>;
  /** The landmark's id — what describes the outline elsewhere (a Day ribbon's
   *  `aria-describedby`). */
  id?: string;
  /** The rows sit on their container (a card's body): no inset, no scroller of their own —
   *  the page scrolls (a catalog itinerary's Days card). Default: an inset scroller (the tour's
   *  sticky outline). */
  flush?: boolean;
  virtualizeAbove?: number;
}

const rowKeyOf = (r: OutlineRowBase) => `${r.kind}:${r.id}`;

/** A drag item's row (`OutlineRowShell` hands its row to dnd-kit as the item's data). */
function rowOf<Row>(data: unknown): Row | undefined {
  return data as Row | undefined;
}

/** The indexes a virtualised list renders: the visible window plus the rows it must keep
 *  (the one tab stop, the row being dragged). */
export function withKept(base: readonly number[], count: number, ...keep: number[]): number[] {
  const extra = keep.filter((i) => i >= 0 && i < count && !base.includes(i));
  return extra.length > 0 ? [...new Set([...base, ...extra])].sort((a, b) => a - b) : [...base];
}

export function RecordOutline<Row extends OutlineRowBase>({
  rows,
  selection,
  onSelect,
  rowHeight,
  renderRow,
  live,
  keyMove,
  onRemoveKey,
  drag,
  labels,
  footer,
  width,
  sx,
  id,
  flush = false,
  virtualizeAbove = VIRTUALIZE_ABOVE,
}: RecordOutlineProps<Row>) {
  const helpId = useId();
  /** What the list assumes before (or without — jsdom) a measured size: a viewport's worth. */
  const fallbackRect = useMemo(() => ({ width: typeof width === 'number' ? width : 0, height: 720 }), [width]);

  // ---- roving focus ----------------------------------------------------------------
  // The focused row is tracked by identity (`day:12`), never by position: a move or a
  // removal re-orders the rows under it.
  const selectedIndex = rows.findIndex((r) => selection !== null && r.kind === selection.kind && r.id === selection.id);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const keyIndex = focusKey === null ? -1 : rows.findIndex((r) => rowKeyOf(r) === focusKey);
  const activeIndex = keyIndex >= 0 ? keyIndex : selectedIndex >= 0 ? selectedIndex : 0;
  const selectionKey = selection ? `${selection.kind}:${selection.id}` : null;
  useEffect(() => {
    if (selectionKey) setFocusKey(selectionKey);
  }, [selectionKey]);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selectIndex = useCallback(
    (index: number) => {
      const r = rowsRef.current[index];
      if (!r) return;
      setFocusKey(rowKeyOf(r));
      onSelect({ kind: r.kind, id: r.id });
    },
    [onSelect],
  );

  // ---- virtualisation --------------------------------------------------------------
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtual = rows.length > virtualizeAbove;
  /** Rows kept mounted outside the visible window: the one tab stop (else Tab skips the
   *  outline once it scrolls away) and the row being dragged. */
  const [dragKey, setDragKey] = useState<string | null>(null);
  const dragIndex = dragKey === null ? -1 : rows.findIndex((r) => rowKeyOf(r) === dragKey);
  const rangeExtractor = useCallback(
    (range: Range) => withKept(defaultRangeExtractor(range), range.count, activeIndex, dragIndex),
    [activeIndex, dragIndex],
  );
  const virtualizer = useVirtualizer({
    count: rows.length,
    enabled: virtual,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => {
      const r = rows[i];
      return r ? rowHeight(r) : 0;
    },
    overscan: 10,
    rangeExtractor,
    // The first frame (and jsdom) measures nothing: assume a viewport's worth.
    initialRect: fallbackRect,
    observeElementRect: (instance, cb) => observeElementRect(instance, (rect) => cb(rect.height > 0 ? rect : fallbackRect)),
  });

  /** Focus a row (by identity), scrolling a virtualised list to it first. */
  const focusRow = useCallback(
    (index: number) => {
      const r = rowsRef.current[index];
      if (!r) return;
      const key = rowKeyOf(r);
      setFocusKey(key);
      if (virtual) virtualizer.scrollToIndex(index, { align: 'auto' });
      requestAnimationFrame(() => {
        scrollRef.current?.querySelector<HTMLElement>(`[data-itin-row="${key}"]`)?.focus();
      });
    },
    [virtual, virtualizer],
  );

  // After a keyboard move or removal: the moved row keeps the focus; a removed row's
  // neighbour (the row now in its place, else the one before) takes it.
  const pendingFocus = useRef<{ key: string } | { index: number } | null>(null);
  useLayoutEffect(() => {
    const p = pendingFocus.current;
    if (!p) return;
    pendingFocus.current = null;
    const index = 'key' in p ? rows.findIndex((r) => rowKeyOf(r) === p.key) : Math.min(p.index, rows.length - 1);
    if (index >= 0) focusRow(index);
  }, [rows, focusRow]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!(e.target instanceof HTMLElement) || !e.target.matches('[role="treeitem"]')) return;
    // The row is the one the key landed on, read from the element — not a remembered index.
    const key = e.target.dataset.itinRow;
    const index = rows.findIndex((r) => rowKeyOf(r) === key);
    const row = rows[index];
    if (!row) return;
    if (e.key === 'ArrowDown' && !e.altKey) {
      e.preventDefault();
      focusRow(Math.min(index + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp' && !e.altKey) {
      e.preventDefault();
      focusRow(Math.max(index - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusRow(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusRow(rows.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectIndex(index);
    } else if (live && keyMove && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && !row.removed && keyMove.accepts(row)) {
      e.preventDefault();
      pendingFocus.current = { key: rowKeyOf(row) };
      if (!keyMove.move(row, e.key === 'ArrowUp' ? -1 : 1)) pendingFocus.current = null;
    } else if (live && onRemoveKey && (e.key === 'Delete' || e.key === 'Backspace') && !row.removed) {
      e.preventDefault();
      pendingFocus.current = { index };
      if (!onRemoveKey(row)) pendingFocus.current = null;
    } else if (live && (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10'))) {
      e.preventDefault();
      e.target.querySelector<HTMLElement>('[data-row-menu]')?.click();
    }
  };

  // ---- drag and drop (pointer; the keyboard path is Alt+arrows and the menus) --------
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const announce = drag?.announce;
  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart: ({ active }) => announce?.('start', rowOf<Row>(active.data.current), undefined, rowsRef.current),
      onDragOver: ({ active, over }) => announce?.('over', rowOf<Row>(active.data.current), rowOf<Row>(over?.data.current), rowsRef.current),
      onDragEnd: ({ active, over }) => announce?.('end', rowOf<Row>(active.data.current), rowOf<Row>(over?.data.current), rowsRef.current),
      onDragCancel: ({ active }) => announce?.('cancel', rowOf<Row>(active.data.current), undefined, rowsRef.current),
    }),
    [announce],
  );
  const onDragStart = (e: DragStartEvent) => {
    const from = rowOf<Row>(e.active.data.current);
    setDragKey(from ? rowKeyOf(from) : null);
  };
  const onDragEnd = (e: DragEndEvent) => {
    setDragKey(null);
    drag?.onDrop(rowOf<Row>(e.active.data.current), rowOf<Row>(e.over?.data.current), rows);
  };
  const dragged = dragIndex >= 0 ? rows[dragIndex] : undefined;
  const overlay = dragged && drag ? drag.overlay(dragged) : null;

  const rowProps = (index: number): OutlineRowProps => ({
    index,
    active: index === activeIndex,
    selected: index === selectedIndex,
    onSelect: selectIndex,
  });

  return (
    <Box
      component="nav"
      id={id}
      aria-label={labels.nav}
      data-itin-outline
      sx={[{ display: 'flex', flexDirection: 'column', minHeight: 0, ...(width !== undefined ? { width } : {}) }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Typography id={helpId} sx={visuallyHidden}>
        {labels.help}
        {live && labels.liveHelp ? ` ${labels.liveHelp}` : ''}
      </Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDragKey(null)}
        accessibility={{ announcements, ...(drag ? { screenReaderInstructions: { draggable: drag.instructions } } : {}) }}
      >
        <Box
          ref={scrollRef}
          role="tree"
          aria-label={labels.tree}
          aria-describedby={helpId}
          data-virtual={virtual || undefined}
          onKeyDown={onKeyDown}
          sx={{ flex: '1 1 auto', minHeight: 0, overflowY: flush ? 'visible' : 'auto', p: flush ? 0 : 0.5, position: 'relative' }}
        >
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }} data-outline-empty>
              {labels.empty}
              {live && labels.liveEmpty ? ` ${labels.liveEmpty}` : ''}
            </Typography>
          ) : virtual ? (
            <Box sx={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map((v) => (
                <Box key={v.key} sx={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${v.start}px)` }}>
                  {renderRow(rows[v.index]!, rowProps(v.index))}
                </Box>
              ))}
            </Box>
          ) : (
            rows.map((r, index) => renderRow(r, rowProps(index)))
          )}
        </Box>
        {/* The dragged row as a floating clone: never clipped by the scroller or a virtual
            row's box, and painted over the rows after it. */}
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {dragged && overlay ? (
              <Paper
                elevation={4}
                data-drag-overlay
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  height: rowHeight(dragged),
                  px: 1,
                  width: typeof width === 'number' ? width - 16 : '100%',
                  cursor: 'grabbing',
                }}
              >
                <DragIndicatorIcon aria-hidden sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" noWrap sx={{ fontWeight: overlay.strong ? 600 : 400 }}>
                  {overlay.name}
                </Typography>
              </Paper>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
      {footer}
    </Box>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
